import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {SidebarService} from './sidebar.service';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {AsyncPipe, NgClass, LowerCasePipe} from '@angular/common';
import {MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelContent,
  MatExpansionPanelHeader} from '@angular/material/expansion';
import {filter, tap, Subscription} from 'rxjs';
import {BreakpointService} from '../../services/breakpoint/breakpoint.service';
import {SidebarSection, SidebarSubItem} from '../../../sidebar-section.provider';
import {environment} from 'src/environments/environment';
import {EmployeeService} from 'src/app/prudent-plus-shared/services/employee/employee.service';
import {MatDialog} from '@angular/material/dialog';
import {WarningModalComponent, WarningModalComponentData} from '../../modals/warning-modal/warning-modal.component';
import small = DialogConfiguration.small;
import {DialogConfiguration} from '../dialog-configuration';
import {InsightsService} from '../../services/insights/insights.service';
import {FeatureFlagsService} from '../../services/feature-flag/feature-flags.service';
import {MatTooltipModule} from '@angular/material/tooltip';
import {UserConfigurationService} from 'src/app/prudent-plus-shared/services/user-configuration/user-configuration.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    NgClass,
    RouterLink,
    AsyncPipe,
    MatExpansionPanelContent,
    MatTooltipModule,
    LowerCasePipe,
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  protected readonly sidebarService = inject(SidebarService);
  protected readonly breakpointService = inject(BreakpointService);
  private readonly router = inject(Router);
  private routerEventsSub?: Subscription;
  private employeeService = inject(EmployeeService);
  private readonly dialog = inject(MatDialog);
  private readonly insightsService = inject(InsightsService);
  protected readonly featureFlagService = inject(FeatureFlagsService);
  private configurationService = inject(UserConfigurationService);

  protected readonly deviceSize$ = inject(BreakpointService).deviceSize$.pipe(
    tap((deviceSize) => {
      this.sidebarService.forceExpand = !deviceSize.isMobile;
    })
  );

  private readonly mobileSidebar = [
    'Dashboard',
    'Your Profile',
    'My Enrolment',
    'Benefits',
    'Claims',
    'Network Hospitals',
    'Marketplace',
    'My Documents',
    'Support',
    'Wellness',
    '2024-2025 claims',
    'Prudent Prime',
  ];

  isSubItemsVisible: boolean = false;
  subItems: SidebarSubItem[] = [];

  expandedSection?: SidebarSection = undefined;
  chosenSection?: SidebarSection = undefined;

  ngOnInit() {
    this.sidebarService.forceExpand = !this.sidebarService.forceExpand;

    // to remove the focus from the sidebar after clicking on any item
    this.routerEventsSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const el = document.activeElement;

        if (el instanceof HTMLElement) el.blur();
      });
  }

  ngOnDestroy() {
    this.routerEventsSub?.unsubscribe();
  }

  mobileSizeMenu(sidebar: SidebarSection[], isMobile: boolean) {
    if (isMobile) {
      let mobileSidebar = sidebar[0].items.filter(
        (mobileView) => this.mobileSidebar.includes(mobileView.title) || mobileView.isCustomItem
      );

      sidebar[0].items = [...mobileSidebar];

      return sidebar;
    }

    return sidebar;
  }

  showSubItem(subItems?: SidebarSubItem[]) {
    this.isSubItemsVisible = true;
    if (subItems) {
      this.subItems = subItems;
    }
  }
  onLeave() {
    this.isSubItemsVisible = false;
  }
  onLeaveSidebar() {
    this.isSubItemsVisible = false;
    this.subItems = [];
  }

  setChosenSection(sidebarSection: SidebarSection, sidebarItem?: SidebarSubItem, event?: MouseEvent) {
    this.chosenSection = sidebarSection;
    if (
      sidebarItem &&
      this.featureFlagService.marketPlaceLocked &&
      sidebarSection.items.some((item) => item.locked) &&
      (sidebarItem.title.toLowerCase().includes('marketplace') || sidebarItem.title.toLowerCase().includes('prime'))
    ) {
      event?.preventDefault();
      const userId = this.employeeService.currentEmployee.id;
      const clientId = this.employeeService.currentEmployee.customerId;
      const hasUserDetails = !!this.configurationService.configuration.userDetails;

      if (sidebarItem.title.toLowerCase().includes('marketplace')) {
        this.insightsService.logEvent('PP_MARKETPLACE_LOCKED', {
          clientId,
          userId,
        });
      } else if (sidebarItem.title.toLowerCase().includes('prime')) {
        this.insightsService.logEvent('PP_PRIME_LOCKED', {
          clientId,
          userId,
        });
      }

      this.dialog.open(
        WarningModalComponent,
        small<WarningModalComponentData>({
          imageSrc: 'assets/marketplace/lock.gif',
          title: sidebarItem.title.toLowerCase().includes('prime') ?
            'Your access to Prudent Prime is locked.' :
            'Your access to Marketplace is locked.',
          subtitle: sidebarItem.title.toLowerCase().includes('prime') ?
            `Prudent Prime isn't open yet - complete your enrolment to start exploring!` :
            `Marketplace isn't open yet - complete your enrolment to start exploring!`,
          continue: 'Ok',
        })
      ).afterClosed()
        .subscribe((result) => {
          if (result === true) {
            if (hasUserDetails) {
              this.router.navigate(['/dashboard/profile']);
            } else {
              this.router.navigate(['/dashboard/enrolment/verify-details']);
            }
          }
        });

      return false;
    }

    return true;
  }

  protected readonly environment = environment;

  icon(sidebarSection: SidebarSection) {
    let url = this.router.url;

    for (let item of sidebarSection.items) {
      if (!item.sidebarSubItem && url.includes(item.routerLink())) {
        return sidebarSection.activeIcon ? sidebarSection.activeIcon : sidebarSection.icon;
      }

      if (item.sidebarSubItem && item.sidebarSubItem.some((subItem) => url.includes(subItem.routerLink()))) {
        return sidebarSection.activeIcon ? sidebarSection.activeIcon : sidebarSection.icon;
      }
    }

    return sidebarSection.icon;
  }

  expandSection(sidebarSection: SidebarSection, sidebarSections: SidebarSection[]) {
    sidebarSections.forEach((section) => {
      section.expanded = section === sidebarSection;
    });
    this.expandedSection = sidebarSection;
  }

  checkSidebarItem(sidebarItem: SidebarSubItem) {
    if (
      this.router.url.includes('dashboard/enrolment') &&
      sidebarItem.title.includes('My Enrolment')
    ) {
      return true;
    } else {
      return this.router.url.includes(
        sidebarItem.routerLink()
      );
    }
  }
}
